import React, { useState } from 'react';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import GradientText from './GradientText.tsx';

// Use a public path for the image
const ModelisticLogoPath = '/images/Modelistic.png';

interface LogoProps {
  size?: 'small' | 'medium' | 'large';
  color?: string;
}

const LogoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  position: 'relative',
  cursor: 'pointer',
  padding: theme.spacing(0.5),
  borderRadius: '50px',
  transition: 'all 0.3s ease',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: '50px',
    padding: '2px',
    background: 'linear-gradient(90deg, #003366, #00CC99, #8A2BE2, #FF6F00)',
    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    WebkitMaskComposite: 'xor',
    maskComposite: 'exclude',
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },
  
  '&:hover::before': {
    opacity: 1,
  },
  
  '&:hover .logo-image': {
    transform: 'scale(1.05)',
    boxShadow: '0 0 15px rgba(138, 43, 226, 0.7)',
  }
}));

const LogoImage = styled('img')(({ theme }) => ({
  borderRadius: '50%',
  objectFit: 'cover',
  transition: 'all 0.3s ease',
}));

const StyledLink = styled(Link)({
  textDecoration: 'none',
  display: 'flex',
  alignItems: 'center',
});

const Logo: React.FC<LogoProps> = ({ size = 'medium', color = '#fff' }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  // Define sizes for different variants
  const sizes = {
    small: {
      logoSize: 20,
    },
    medium: {
      logoSize: 32,
    },
    large: {
      logoSize: 48,
    },
  };

  const { logoSize } = sizes[size];

  return (
    <StyledLink to="/">
      <LogoContainer 
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <LogoImage 
          className="logo-image"
          src={ModelisticLogoPath} 
          alt="Eterna AI Logo" 
          width={logoSize} 
          height={logoSize}
          sx={{ 
            boxShadow: isHovered 
              ? '0 0 15px rgba(138, 43, 226, 0.7)' 
              : '0 0 10px rgba(138, 43, 226, 0.5)',
          }}
        />
        <GradientText 
          variant={size === 'small' ? 'body1' : size === 'medium' ? 'h6' : 'h4'} 
          gradient={isHovered ? 'animated' : 'primary'}
          sx={{ 
            fontWeight: 600, 
            letterSpacing: '0.5px',
          }}
        >
          Eterna AI
        </GradientText>
      </LogoContainer>
    </StyledLink>
  );
};

export default Logo; 